import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PickersDay } from '@mui/x-date-pickers';
import { format } from 'date-fns';
import { useLanguage } from './LanguageContext';
import { getTranslation } from './translations';
import LanguageSelector from './LanguageSelector';

function Register() {
  const { language } = useLanguage();
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  // Validation functions
  const validateFirstname = (name) => {
    if (!name || name.trim().length < 2) {
      return getTranslation('firstNameError', language);
    }
    return '';
  };

  const validateLastname = (name) => {
    if (!name || name.trim().length < 2) {
      return getTranslation('lastNameError', language);
    }
    return '';
  };

  const validatePassword = (pwd) => {
    if (!pwd || pwd.length < 8) {
      return getTranslation('passwordError', language);
    }
    if (!/[0-9]/.test(pwd)) {
      return getTranslation('passwordDigitError', language);
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd)) {
      return getTranslation('passwordSpecialError', language);
    }
    return '';
  };

  const validateForm = () => {
    const newErrors = {};
    
    const firstnameError = validateFirstname(firstname);
    if (firstnameError) newErrors.firstname = firstnameError;
    
    const lastnameError = validateLastname(lastname);
    if (lastnameError) newErrors.lastname = lastnameError;
    
    const passwordError = validatePassword(password);
    if (passwordError) newErrors.password = passwordError;
    
    if (password !== repeatPassword) {
      newErrors.repeatPassword = getTranslation('passwordMismatchError', language);
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setErrors({});
    
    if (!validateForm()) {
      return;
    }
    try {
      const response = await fetch('/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          firstname, 
          lastname, 
          email, 
          password, 
          repeat_password: repeatPassword,
          language: language
        }),
      });
      if (response.ok) {
        // Redirect to success page instead of showing message
        navigate('/registration-success');
      } else {
        const data = await response.json();
        let errorMsg = getTranslation('registrationFailed', language);
        if (Array.isArray(data.detail)) {
          errorMsg = data.detail.map(err => err.msg).join(' ');
        } else if (typeof data.detail === 'string') {
          errorMsg = data.detail;
        }
        setMessage(errorMsg);
      }
    } catch (error) {
      setMessage(getTranslation('serverError', language));
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: '#f0f8ff',
      display: 'flex', 
      flexDirection: 'column',
      alignItems: 'center', 
      justifyContent: 'center', 
      padding: window.innerWidth <= 600 ? '20px 20px' : '40px 0',
      fontFamily: 'Nunito, Arial, sans-serif',
      position: 'relative'
    }}>
      {/* Language Selector - Top Right */}
      <div style={{
        position: 'absolute',
        top: '20px',
        right: '20px',
        zIndex: 1000
      }}>
        <LanguageSelector />
      </div>

      {/* Main Title */}
      <h1 style={{
        fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
        fontWeight: 'bold',
        color: '#7c3aed',
        margin: '0 0 16px 0',
        textAlign: 'center',
        padding: window.innerWidth <= 600 ? '0 20px' : '0'
      }}>
        {getTranslation('shootMe', language)}
      </h1>
      
      {/* Subtitle */}
      <p style={{
        fontSize: window.innerWidth <= 600 ? '1rem' : '1.2rem',
        color: '#6b7280',
        margin: '0 0 48px 0',
        textAlign: 'center',
        maxWidth: window.innerWidth <= 600 ? '90%' : '600px',
        lineHeight: '1.6',
        padding: window.innerWidth <= 600 ? '0 20px' : '0'
      }}>
        {getTranslation('appDescription', language)}
      </p>

      <div style={{
        background: 'white',
        borderRadius: window.innerWidth <= 600 ? 16 : 20,
        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.2)',
        maxWidth: window.innerWidth <= 600 ? '95%' : 400,
        width: window.innerWidth <= 600 ? '95%' : 400,
        padding: window.innerWidth <= 600 ? 24 : 32,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
      }}>
        <h2 style={{ 
          color: '#7c3aed', 
          marginBottom: 24, 
          fontFamily: 'Nunito, Arial, sans-serif', 
          fontWeight: 700, 
          letterSpacing: 1,
          fontSize: window.innerWidth <= 600 ? '1.5rem' : '1.8rem',
          textAlign: 'center'
        }}>{getTranslation('registration', language)}</h2>
        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          <div style={{ marginBottom: window.innerWidth <= 600 ? 16 : 18 }}>
            <label style={{ 
              color: '#4b4b4b', 
              fontWeight: 500,
              fontSize: window.innerWidth <= 600 ? '14px' : '16px'
            }}>{getTranslation('firstName', language)}:</label><br />
            <input
              type="text"
              value={firstname}
              onChange={e => {
                setFirstname(e.target.value);
                if (errors.firstname) {
                  const newErrors = { ...errors };
                  delete newErrors.firstname;
                  setErrors(newErrors);
                }
              }}
              onBlur={() => {
                const error = validateFirstname(firstname);
                if (error) {
                  setErrors(prev => ({ ...prev, firstname: error }));
                }
              }}
              required
              style={{ 
                width: '100%', 
                padding: window.innerWidth <= 600 ? 12 : 10, 
                borderRadius: 8, 
                border: errors.firstname ? '2px solid #ef4444' : '1px solid #bdbdbd', 
                marginTop: 4, 
                fontSize: window.innerWidth <= 600 ? '16px' : '16px',
                boxSizing: 'border-box'
              }}
            />
            {errors.firstname && (
              <div style={{ 
                color: '#ef4444', 
                fontSize: '12px', 
                marginTop: '4px' 
              }}>
                {errors.firstname}
              </div>
            )}
          </div>
          <div style={{ marginBottom: window.innerWidth <= 600 ? 16 : 18 }}>
            <label style={{ 
              color: '#4b4b4b', 
              fontWeight: 500,
              fontSize: window.innerWidth <= 600 ? '14px' : '16px'
            }}>{getTranslation('lastName', language)}:</label><br />
            <input
              type="text"
              value={lastname}
              onChange={e => {
                setLastname(e.target.value);
                if (errors.lastname) {
                  const newErrors = { ...errors };
                  delete newErrors.lastname;
                  setErrors(newErrors);
                }
              }}
              onBlur={() => {
                const error = validateLastname(lastname);
                if (error) {
                  setErrors(prev => ({ ...prev, lastname: error }));
                }
              }}
              required
              style={{ 
                width: '100%', 
                padding: window.innerWidth <= 600 ? 12 : 10, 
                borderRadius: 8, 
                border: errors.lastname ? '2px solid #ef4444' : '1px solid #bdbdbd', 
                marginTop: 4, 
                fontSize: window.innerWidth <= 600 ? '16px' : '16px',
                boxSizing: 'border-box'
              }}
            />
            {errors.lastname && (
              <div style={{ 
                color: '#ef4444', 
                fontSize: '12px', 
                marginTop: '4px' 
              }}>
                {errors.lastname}
              </div>
            )}
          </div>
          <div style={{ marginBottom: window.innerWidth <= 600 ? 16 : 18 }}>
            <label style={{ 
              color: '#4b4b4b', 
              fontWeight: 500,
              fontSize: window.innerWidth <= 600 ? '14px' : '16px'
            }}>{getTranslation('email', language)}:</label><br />
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              style={{ 
                width: '100%', 
                padding: window.innerWidth <= 600 ? 12 : 10, 
                borderRadius: 8, 
                border: '1px solid #bdbdbd', 
                marginTop: 4, 
                fontSize: window.innerWidth <= 600 ? '16px' : '16px',
                boxSizing: 'border-box'
              }}
            />
          </div>
          <div style={{ marginBottom: window.innerWidth <= 600 ? 16 : 18 }}>
            <label style={{ 
              color: '#4b4b4b', 
              fontWeight: 500,
              fontSize: window.innerWidth <= 600 ? '14px' : '16px'
            }}>{getTranslation('password', language)}:</label><br />
            <input
              type="password"
              value={password}
              onChange={e => {
                setPassword(e.target.value);
                if (errors.password) {
                  const newErrors = { ...errors };
                  delete newErrors.password;
                  setErrors(newErrors);
                }
              }}
              onBlur={() => {
                const error = validatePassword(password);
                if (error) {
                  setErrors(prev => ({ ...prev, password: error }));
                }
              }}
              required
              style={{ 
                width: '100%', 
                padding: window.innerWidth <= 600 ? 12 : 10, 
                borderRadius: 8, 
                border: errors.password ? '2px solid #ef4444' : '1px solid #bdbdbd', 
                marginTop: 4, 
                fontSize: window.innerWidth <= 600 ? '16px' : '16px',
                boxSizing: 'border-box'
              }}
            />
            {errors.password && (
              <div style={{ 
                color: '#ef4444', 
                fontSize: '12px', 
                marginTop: '4px' 
              }}>
                {errors.password}
              </div>
            )}
            <div style={{ 
              color: '#6b7280', 
              fontSize: '11px', 
              marginTop: '4px',
              lineHeight: '1.4'
            }}>
              {getTranslation('passwordHint', language)}
            </div>
          </div>
          <div style={{ marginBottom: window.innerWidth <= 600 ? 16 : 18 }}>
            <label style={{ 
              color: '#4b4b4b', 
              fontWeight: 500,
              fontSize: window.innerWidth <= 600 ? '14px' : '16px'
            }}>{getTranslation('repeatPassword', language)}:</label><br />
            <input
              type="password"
              value={repeatPassword}
              onChange={e => {
                setRepeatPassword(e.target.value);
                if (errors.repeatPassword) {
                  const newErrors = { ...errors };
                  delete newErrors.repeatPassword;
                  setErrors(newErrors);
                }
              }}
              onBlur={() => {
                if (password !== repeatPassword) {
                  setErrors(prev => ({ ...prev, repeatPassword: getTranslation('passwordMismatchError', language) }));
                }
              }}
              required
              style={{ 
                width: '100%', 
                padding: window.innerWidth <= 600 ? 12 : 10, 
                borderRadius: 8, 
                border: errors.repeatPassword ? '2px solid #ef4444' : '1px solid #bdbdbd', 
                marginTop: 4, 
                fontSize: window.innerWidth <= 600 ? '16px' : '16px',
                boxSizing: 'border-box'
              }}
            />
            {errors.repeatPassword && (
              <div style={{ 
                color: '#ef4444', 
                fontSize: '12px', 
                marginTop: '4px' 
              }}>
                {errors.repeatPassword}
              </div>
            )}
          </div>
          <button type="submit" style={{
            width: '100%',
            padding: window.innerWidth <= 600 ? '14px 0' : '12px 0',
            borderRadius: 8,
            border: 'none',
            background: '#7c3aed',
            color: 'white',
            fontWeight: 700,
            fontSize: window.innerWidth <= 600 ? '16px' : '18px',
            letterSpacing: 1,
            boxShadow: '0 4px 12px rgba(124, 58, 237, 0.3)',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            marginBottom: 8
          }}>{getTranslation('registerButton', language)}</button>
        </form>
        {message && <div style={{ 
          marginTop: 18, 
          color: message.includes('success') ? '#2e7d32' : '#c62828', 
          fontWeight: 500,
          fontSize: window.innerWidth <= 600 ? '14px' : '16px',
          textAlign: 'center',
          padding: window.innerWidth <= 600 ? '0 10px' : '0'
        }}>{message}</div>}
      </div>
    </div>
  );
}

export default Register; 